// Leads › bulk bar › Add to campaign… (owner, 2026-11-03: "select multiple leads
// on the lead page and say add to a campaign"). The picked leads start a new
// campaign (its form opens with ?leads=…) or join a draft's hand-picked list.
// Only a draft: scheduling a campaign approved who's in it, so a scheduled one
// takes no more. Every audience rule still applies to a picked lead.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage } from '@/api/client'
import { Button, Dialog, FormField, InlineAlert, SelectInput, useToast } from '@/components/ui'
import { invalidateQueries, useQuery } from '@/lib/query'

export function AddToCampaignDialog({ open, ids, onClose, onDone }: { open: boolean; ids: number[]; onClose: () => void; onDone: () => void }) {
  const navigate = useNavigate()
  const toast = useToast()
  const { data: campaigns } = useQuery(open ? ['growth', 'campaigns'] : null, (signal) => api.growth.campaigns.list(signal))
  const drafts = (campaigns ?? []).filter((c) => c.status === 'draft')
  const [target, setTarget] = useState('new')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const noun = `${ids.length} ${ids.length === 1 ? 'lead' : 'leads'}`

  const go = async () => {
    setProblem(null)
    if (target === 'new') {
      onDone()
      navigate(`/growth/campaigns/new?leads=${ids.join(',')}`)
      return
    }
    setBusy(true)
    try {
      const r = await api.growth.campaigns.addLeads(Number(target), ids)
      invalidateQueries('growth')
      toast.success(r.added === ids.length ? `Added ${noun} to ${r.campaign.name}.` : `Added ${r.added} to ${r.campaign.name}. The rest were already in it.`)
      onDone()
      navigate(`/growth/campaigns/${r.campaign.id}/edit`)
    } catch (err) {
      setProblem(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Add ${noun} to a campaign`}
      description="Only these leads, with the campaign's filters still applying to them: one that isn't verified or active is left out unless the campaign chooses it."
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void go()}>
            {target === 'new' ? 'Start a campaign' : 'Add them'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <FormField label="Add to" hint={drafts.length ? 'A campaign that is scheduled takes no more leads: scheduling approved who is in it.' : 'No drafts yet: a campaign that is scheduled takes no more leads.'}>
          {({ id, describedBy }) => (
            <SelectInput
              id={id}
              aria-describedby={describedBy}
              value={target}
              options={[{ value: 'new', label: 'A new campaign' }, ...drafts.map((c) => ({ value: String(c.id), label: `${c.name} (draft)` }))]}
              onChange={(e) => setTarget(e.target.value)}
            />
          )}
        </FormField>
        {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
      </div>
    </Dialog>
  )
}
